/**
 * Shared presentational ticker marquee - PGF Draft Board Sweep Item 1.
 * Both `DraftBoardChatTicker` (auction) and `SnakeDraftBoardTicker` own
 * their own data logic (chat ingestion, filler generation, dedup) and
 * pass a pre-built `queue` in here. This component owns the RENDERING
 * and the RELEASE TIMING of that queue.
 *
 * [Stabilization fix - Feb 2026] Real stock-ticker motion. The previous
 * keyframe (`translate(100%, -50%) -> translate(-100%, -50%)`) used
 * percentages relative to the ENTRY's own width (how CSS `transform:
 * translate()` percentages work), not the ticker bar's width - a narrow
 * entry (most of them) only travelled ~2x its own width and never
 * actually reached the right edge or cleared the left edge, so entries
 * visibly popped in/out mid-bar instead of sweeping across it. Fixed by
 * anchoring the entry's start position with `left: 100%` (container-
 * relative, off-screen right) and animating `transform: translateX()` by
 * a large, viewport-relative distance that's always enough to clear the
 * bar's full width regardless of container size or entry width.
 *
 * [Snake Board Fixes items 4 + 6] Release timing is WIDTH-DERIVED, not
 * time-derived. The old fixed 500ms stagger lived in the two callers and
 * assumed every entry was roughly the same width, so a long chat message
 * was still on screen when the next entry launched and the two visibly
 * overlapped. Now: each entry is measured after it mounts, and the next
 * entry is not released until the previous one has travelled its OWN
 * rendered width plus `ENTRY_GAP_PX`, at the marquee's actual pixel
 * velocity. Traversal slowed to 17s (was 14s) for readability. The bar
 * also sits above the round-transition modal backdrop (z-index 50 vs the
 * modal's 40) so it stays visible and scrolling during the countdown,
 * and because it paints above the backdrop it is never blurred by it.
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";

const TICKER_TRAVERSAL_MS = 17000;
// Keep in sync with the keyframe's translate distance below.
const TICKER_TRAVEL_VW = 220;
// Clear space demanded between the tail of one entry and the nose of the next.
const ENTRY_GAP_PX = 80;
// Above BetweenRoundsCountdownModal / NominationModal (both z-40).
const TICKER_Z_INDEX = 50;

function pixelVelocity() {
  const viewportW = typeof window === "undefined" ? 1920 : window.innerWidth || 1920;
  return ((TICKER_TRAVEL_VW / 100) * viewportW) / TICKER_TRAVERSAL_MS;
}

export default function DraftMarquee({
  queue,
  onEntryAnimationEnd,
  isDark,
  testId = "draft-board-chat-ticker",
}) {
  const rootBg = isDark ? "#0A0A0A" : "#F5F5F5";
  const rootBorder = isDark ? "#2E2E33" : "#D8D8D8";
  const bodyColor = isDark ? "#F5F5F5" : "#0A0A0A";

  // id -> animation-delay (ms) assigned once, after the entry is measured.
  const delaysRef = useRef(new Map());
  // Absolute ms timestamp at which the next entry is allowed to launch.
  const nextReleaseAtRef = useRef(0);
  const elRefs = useRef(new Map());
  const [, setAssignedCount] = useState(0);

  // Inject keyframes once (side effect, not a computed value → useEffect).
  useEffect(() => {
    if (typeof document === "undefined") return;
    const el = document.getElementById("pgf-ticker-keyframes") || document.createElement("style");
    el.id = "pgf-ticker-keyframes";
    el.textContent = `
      @keyframes pgf-ticker-scroll {
        from { transform: translate(0%, -50%); }
        to   { transform: translate(-${TICKER_TRAVEL_VW}vw, -50%); }
      }
    `;
    if (!el.isConnected) document.head.appendChild(el);
  }, []);

  // Measure each newly-mounted entry and chain its release off the width
  // of the one before it.
  useLayoutEffect(() => {
    const liveIds = new Set(queue.map((e) => e.id));
    delaysRef.current.forEach((_, id) => {
      if (!liveIds.has(id)) {
        delaysRef.current.delete(id);
        elRefs.current.delete(id);
      }
    });

    let assigned = 0;
    const velocity = pixelVelocity();
    queue.forEach((entry) => {
      if (delaysRef.current.has(entry.id)) return;
      const el = elRefs.current.get(entry.id);
      if (!el) return;
      const now = Date.now();
      const startAt = Math.max(now, nextReleaseAtRef.current);
      const width = el.offsetWidth || 0;
      nextReleaseAtRef.current = startAt + (width + ENTRY_GAP_PX) / velocity;
      delaysRef.current.set(entry.id, startAt - now);
      assigned += 1;
    });
    if (assigned) setAssignedCount((n) => n + assigned);
  }, [queue]);

  return (
    <div
      data-testid={testId}
      className="relative w-full overflow-hidden shrink-0"
      style={{
        height: 56,
        background: rootBg,
        borderTop: `1px solid ${rootBorder}`,
        zIndex: TICKER_Z_INDEX,
      }}
      aria-label="Draft board chat ticker"
    >
      {queue.map((entry) => {
        const delayMs = delaysRef.current.get(entry.id);
        return (
          <div
            key={entry.id}
            ref={(el) => {
              if (el) elRefs.current.set(entry.id, el);
            }}
            data-testid={`ticker-entry-${entry.type}`}
            onAnimationEnd={() => onEntryAnimationEnd(entry.id)}
            className="absolute whitespace-nowrap font-body"
            style={{
              left: "100%",
              top: "50%",
              fontSize: 26,
              color: bodyColor,
              // Until the entry has been measured it has no animation, so
              // it waits off-screen right at `left: 100%`.
              animation:
                delayMs === undefined
                  ? undefined
                  : `pgf-ticker-scroll ${TICKER_TRAVERSAL_MS}ms linear ${delayMs}ms forwards`,
              willChange: "transform",
            }}
          >
            {entry.teamName ? (
              <>
                <span
                  data-testid="ticker-entry-team"
                  style={{
                    color: entry.teamColor,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  {entry.teamName}
                </span>
                <span style={{ color: bodyColor }}>{`: ${entry.body}`}</span>
              </>
            ) : (
              <span style={{ color: bodyColor, fontWeight: 700, letterSpacing: "0.05em" }}>
                {entry.body}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
